import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import Navbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import { Syringe, Stethoscope, FlaskConical, HeartPulse, ArrowRight, Baby, Plane, UserRound } from "lucide-react";

const ICONS = {
  vaccination: Syringe,
  doctor: Stethoscope,
  lab: FlaskConical,
  checkup: HeartPulse,
};

const FALLBACK = [
  { id: "vaccination", type: "vaccination", name: "At-Home Vaccinations", description: "Certified nurses with cold-chain safe vaccines at your doorstep." },
  { id: "doctor", type: "doctor", name: "Doctor Consultations", description: "Doctor-verified care whenever required." },
  { id: "lab", type: "lab", name: "Lab Tests", description: "At-home sample collection and diagnostics." },
  { id: "checkup", type: "checkup", name: "Health Checkups", description: "Comprehensive checkup packages for the family." },
];

const VAX_CATEGORIES = [
  { id: "paediatric", label: "Paediatric", icon: Baby, blurb: "Age-appropriate vaccines for children, at home." },
  { id: "adult", label: "Adult", icon: UserRound, blurb: "Flu, HPV, hepatitis and routine adult protection." },
  { id: "elderly", label: "Elderly", icon: HeartPulse, blurb: "Pneumococcal, shingles and senior care schedules." },
  { id: "travel", label: "Travel", icon: Plane, blurb: "Destination-ready vaccines before you fly." },
];

export default function ServicesPage() {
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    axios.get(`${config.API}/services`).then(({ data }) => {
      if (data.items?.length) setItems(data.items);
    }).catch(() => {});
  }, []);

  const hub = FALLBACK.map((f) => {
    const match = items.find((i) => i.type === f.type) || f;
    return { ...f, ...match };
  });

  return (
    <div className="min-h-screen bg-[#fdfcfb]" data-testid="services-page">
      <Navbar />
      <section className="pt-28 pb-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-widest text-[#f37721] mb-3">Services</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>
          Healthcare at your doorstep
        </h1>
        <p className="mt-4 text-[#3e195e] max-w-2xl" style={{ fontFamily: "Montserrat" }}>
          Book vaccinations, doctor consultations, lab tests and health checkups — one shared booking system for web and mobile.
        </p>
      </section>

      <section className="pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-[#311b52] mb-6" style={{ fontFamily: "Poppins" }}>Vaccination by life stage</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {VAX_CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.id} to={`/booking?type=vaccination&category=${c.id}`}
                className="bg-white rounded-3xl p-6 border border-[#673c96]/10 hover:border-[#f37721]/40 transition-colors"
                data-testid={`vax-category-${c.id}`}>
                <div className="w-10 h-10 rounded-2xl bg-[#f37721]/10 text-[#f37721] flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-[#311b52]">{c.label}</h3>
                <p className="mt-2 text-sm text-[#673c96]">{c.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#f37721]">Book <ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            );
          })}
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/vaccination-catalogue" className="text-sm font-semibold text-[#673c96] underline">Browse full catalogue</Link>
          <Link to="/login" className="text-sm font-semibold text-[#f37721]">Sign in to book in-app →</Link>
        </div>
      </section>

      <section className="pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-[#311b52] mb-6" style={{ fontFamily: "Poppins" }}>All services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {hub.map((s) => {
            const Icon = ICONS[s.type] || Syringe;
            return (
              <Link
                key={s.type}
                to={s.type === "vaccination" ? "/vaccination-catalogue" : `/booking?type=${s.type}`}
                className="group bg-white rounded-3xl p-8 border border-[#673c96]/10 hover:shadow-lg transition-all"
                data-testid={`service-card-${s.type}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-[#f37721]/10 flex items-center justify-center text-[#f37721] mb-4">
                  <Icon className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{s.name}</h2>
                <p className="mt-2 text-[#673c96]" style={{ fontFamily: "Montserrat" }}>{s.description}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[#f37721] font-semibold">
                  Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>
      <Footer />
    </div>
  );
}
